"use client"

import { ReactNode, useRef, useState } from "react"

// Accessibility:
// https://www.w3.org/WAI/ARIA/apg/patterns/accordion/

import { useId } from "react"
import { CollapseIcon, ExpandIcon } from "src/components/icons"
import { useWindowEvent } from "src/hooks/useWindowEvent"
import { scrollIntoView } from "src/utilities/scroll"
import { AnimateHeight } from "../unstyled/AnimateHeight"

const goToAccordionItemEventName = "toggle-accordion-item" as any

type GoToAccordionItemEventDetail = {
  heading: string
}

export function goToAccordionItem(heading: string) {
  const detail: GoToAccordionItemEventDetail = { heading }

  window.dispatchEvent(new CustomEvent(goToAccordionItemEventName, { detail }))
}

export type AccordionItemData = {
  heading: string
  children: ReactNode
}

export type AccordionItemProps = AccordionItemData & {
  expanded: boolean
  onToggle: () => void
}

const AccordionItem = ({ heading, children, expanded, onToggle }: AccordionItemProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const buttonId = useId()
  const panelId = useId()

  useWindowEvent(goToAccordionItemEventName, (event: CustomEvent<GoToAccordionItemEventDetail>) => {
    if (event.detail.heading === heading) {
      if (!expanded) {
        onToggle()
      }
      scrollIntoView(buttonRef.current)
    }
  })

  const Icon = expanded ? CollapseIcon : ExpandIcon

  return (
    <li className="group border-b last:border-0">
      <h3 className="m-0 py-0 text-xl font-medium">
        <button
          ref={buttonRef}
          type="button"
          id={buttonId}
          aria-controls={panelId}
          aria-expanded={expanded}
          className="hover:text-brand-primary flex w-full items-center justify-between gap-4 rounded-xl py-3 text-left"
          onClick={onToggle}
        >
          <span className="py-2">{heading}</span>
          <Icon className="text-brand-primary size-6" />
        </button>
      </h3>

      <div id={panelId} aria-labelledby={buttonId}>
        <AnimateHeight expanded={expanded}>
          <div className="mr-auto ml-0 pb-6">{children}</div>
        </AnimateHeight>
      </div>
    </li>
  )
}

export type AccordionProps = {
  items: AccordionItemData[]
}

export const Accordion = ({ items }: AccordionProps) => {
  const [expandedItem, setExpandedItem] = useState<AccordionItemData | undefined>()

  return (
    <ul>
      {items.map((item, index) => {
        return (
          <AccordionItem
            key={index}
            {...item}
            expanded={item === expandedItem}
            onToggle={() => (item === expandedItem ? setExpandedItem(undefined) : setExpandedItem(item))}
          />
        )
      })}
    </ul>
  )
}
